import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { PRONTUARIO_VIEW_TRANSITION_EASE } from '../../constants/prontuarioTheme';

const LIVE_WORD_REVEAL_MS = 58;

const TranscriptionLiveDots = () => (
	<span
		className="ml-1 inline-flex items-center gap-[3px] align-middle"
		aria-hidden
	>
		{[0, 1, 2].map((index) => (
			<span
				key={index}
				className="size-1 rounded-full bg-slate-400/90 motion-safe:animate-pulse"
				style={{ animationDelay: `${index * 180}ms` }}
			/>
		))}
	</span>
);

type SessionCallTranscriptionBubbleTextProps = {
	text: string;
	isLive?: boolean;
	isPanelOpen?: boolean;
	/** Palavras já exibidas antes de abrir o painel (ou ao abrir). */
	baselineWordCount?: number;
};

const SessionCallTranscriptionBubbleText = ({
	text,
	isLive = false,
	isPanelOpen = true,
	baselineWordCount = 0,
}: SessionCallTranscriptionBubbleTextProps) => {
	const words = useMemo(
		() => (text.trim() ? text.trim().split(/\s+/) : []),
		[text],
	);
	const shouldAnimate = isLive && isPanelOpen;
	const [visibleCount, setVisibleCount] = useState(() =>
		shouldAnimate
			? Math.min(baselineWordCount, words.length)
			: words.length,
	);
	const visibleCountRef = useRef(visibleCount);

	useEffect(() => {
		visibleCountRef.current = visibleCount;
	}, [visibleCount]);

	useEffect(() => {
		if (words.length === 0) {
			setVisibleCount(0);
			return;
		}

		if (!shouldAnimate) {
			setVisibleCount(words.length);
			return;
		}

		const targetStart = Math.min(baselineWordCount, words.length);
		if (visibleCountRef.current < targetStart) {
			setVisibleCount(targetStart);
			return;
		}

		if (visibleCountRef.current >= words.length) return;

		const intervalId = window.setInterval(() => {
			setVisibleCount((current) => {
				if (current >= words.length) {
					window.clearInterval(intervalId);
					return current;
				}
				return current + 1;
			});
		}, LIVE_WORD_REVEAL_MS);

		return () => window.clearInterval(intervalId);
	}, [baselineWordCount, shouldAnimate, words.length, text]);

	return (
		<span className="whitespace-pre-wrap break-words">
			{words.slice(0, visibleCount).map((word, index) => {
				const content = (
					<>
						{index > 0 ? '\u00A0' : ''}
						{word}
					</>
				);
				const isNewestAnimated =
					shouldAnimate &&
					index === visibleCount - 1 &&
					index >= baselineWordCount;

				if (!isNewestAnimated) {
					return (
						<span key={`${index}-${word}`} className="inline">
							{content}
						</span>
					);
				}

				return (
					<motion.span
						key={`${index}-${word}`}
						initial={{ opacity: 0, y: 3 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{
							duration: 0.3,
							ease: [...PRONTUARIO_VIEW_TRANSITION_EASE],
						}}
						className="inline"
					>
						{content}
					</motion.span>
				);
			})}
			{isLive && isPanelOpen ? <TranscriptionLiveDots /> : null}
		</span>
	);
};

export { SessionCallTranscriptionBubbleText };
